import { vec3 } from 'gl-matrix';
import { Modality } from '~/domain/dicom/modality';
import { fusionColorMappingById } from '~/domain/viewport/fusionBlend';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { gl } from '../../Engine';
import { RenderStorage } from '../../core/render/RenderStorage';
import {
    ImageRenderer,
    ImageRenderingOptions,
    ImageViewMode,
} from '../../core/render/renderers/ImageRenderer';
import { Environment } from '../../data/Environment';
import { ImageObject } from '../../data/image/ImageObject';
import { TextureView } from '../TextureView';
import {
    checkerboardPatternPx,
    CheckerboardPatternPx,
} from './checkerboardPattern';
import { fusedPairOf, resolveFusedImageSlots } from './fusedImageDisplay';
import { Layer } from './Layer';
import { LayerBit } from './LayerBit';

/** How a fused pair is mixed and tinted, as an Environment holds it. */
type PairLook = Pick<
    Environment,
    | 'viewMode'
    | 'colorMask'
    | 'secondaryColorMask'
    | 'petFusionColorMapTypeIdx'
    | 'checkerboardTileSizeMm'
>;

/** The colour mask of an image drawn in its own grey ramp. */
const UNTINTED = vec3.fromValues(1, 1, 1);

/**
 * {@link ImageLayer} provides common properties of {@link Layer Layers} containing {@link ImageObject}data.\
 * {@link ImageLayer ImageLayers} have {@link internalFormat} of type {@link gl.RGBA8} and\
 * {@link format} of type {@link gl.RGBA}.
 */
export class ImageLayer extends Layer<ImageObject> {
    /**
     * ImageLayer constructor
     * @param parent {@link TextureView} on which this {@link ImageLayer} resides.
     */
    public constructor(parent: TextureView, env: Environment) {
        super(parent, env);
    }

    /**
     * The internal format of {@link ImageLayer}s {@link Layer._texture texture}.\
     * {@link ImageLayer ImageLayers} use this format to support colors.\
     * The number of {@link TextureView Views} & {@link Layer Layers} is so small
     * that the memory difference between 8bit and 32bit view textures does not make much difference.
     */
    public get internalFormat(): number {
        return gl.RGBA8;
    }

    /**
     * The format of the {@link Layer._texture texture} this {@link Layer} defines.
     */
    public get format(): number {
        return gl.RGBA;
    }

    /**
     * The {@link LayerBit.IMAGE_LAYER_BIT}
     */
    public get layerBit(): number {
        return LayerBit.IMAGE_LAYER_BIT;
    }

    /**
     * Method to retrieve the {@link ImageObject Images} currently loaded in the {@link Environment}.
     * @param environment The {@link Environment} from which the {@link ImageObject ImageObjects} are loaded.
     * @returns An {@link Array} of all the {@link ImageObject ImageObjects} currently loaded in the {@link environment}.
     */
    public getTargets(environment: Environment): ImageObject[] {
        const pair = fusedPairOf({
            environment,
            viewFusion: this.getView().viewFusion,
        });
        const images: ImageObject[] = [];
        if (pair.image) {
            images.push(pair.image);
        }
        if (pair.secondaryImage) {
            images.push(pair.secondaryImage);
        }
        return images;
    }

    /**
     * Method to draw the images on this {@link Layer Layer's} texture.\
     * Images are retrieved directly from the environment rather than using the targets parameter,
     * since the system explicitly supports only primary and secondary images with specific fusion modes.
     *
     * Always binds and clears the texture first so that stale image data is removed from the
     * GPU framebuffer when images are unloaded.
     *
     * @param _images Unused. Required by {@link Layer} interface but images are retrieved from environment.
     */
    protected draw(images: ImageObject[]): void {
        // Always bind and clear the texture first, even if there's no image to render.
        // This ensures stale image data is cleared from the GPU framebuffer on unload.
        this.bindAndClearTexture();

        const imageRenderer = RenderStorage.getRenderer(ImageRenderer);
        // PET may sit on either side of the fusion and the renderer must know to
        // engage the colormap shader in either case — but that is a question
        // about the set, not about which slot each image occupies, so the
        // targets answer it. They are exactly the two env slots minus the empty
        // ones ({@link getTargets}), which is why rebuilding the pair here was
        // redundant. The render call below still reads the slots directly: it
        // is the one part that does care which is primary and which secondary,
        // and a flat list cannot say.
        const slots = resolveFusedImageSlots(
            this.getView().fusedImageDisplay,
            fusedPairOf(this.getView()),
            this.getView().alignedImageKey
        );
        const look = this.pairLook();
        const drawn = slots.primaryImage ? [slots.primaryImage] : [];
        const ifPetImageFused = (
            this.getView().alignedImageKey === undefined ? images : drawn
        ).some((image) => image.parent.seriesMetadata.modality === Modality.PT);
        const viewMode = slots.showsBlend
            ? look.viewMode
            : ImageViewMode.AlphaBlending;
        const renderingOptions: ImageRenderingOptions = {
            alpha: slots.alpha,
            checkerboard:
                viewMode === ImageViewMode.Checkerboard
                    ? this.checkerboardPattern()
                    : undefined,
            viewMode,
            colorMask:
                slots.primarySlotMask === 'none'
                    ? UNTINTED
                    : slots.primarySlotMask === 'secondary'
                      ? look.secondaryColorMask
                      : look.colorMask,
            secondaryColorMask: look.secondaryColorMask,
            ifPetImageFused: ifPetImageFused,
            petFusionColorMapTypeIdx: look.petFusionColorMapTypeIdx,
        };
        imageRenderer.render(
            this.getView(),
            slots.primaryImage,
            slots.secondaryImage,
            renderingOptions
        );
    }

    /**
     * Where this view's checkerboard tiles meet and how big they are, in
     * fragment pixels: the crosshair projected into the view, and the
     * Environment's tile length at the view's zoom. Only the checkerboard
     * shader consumes it, so it is computed only in
     * {@link ImageViewMode.Checkerboard}; the projection is not free on
     * the per-render path. Undefined when the view is not a
     * {@link PlaneView}, which has no projection to place a pattern with.
     */
    private checkerboardPattern(): CheckerboardPatternPx | undefined {
        const view = this.getView();
        if (!(view instanceof PlaneView)) {
            return undefined;
        }
        return checkerboardPatternPx({
            crosshairCanvasPx: view.canvasCoordsFromPatientCoords(
                this.env.planeIntersectionPoint.coordinates
            ),
            viewSizePx: view.size,
            mmPerPx: view.resolution,
            tileSizeMm: this.pairLook().checkerboardTileSizeMm,
        });
    }

    /**
     * How the pair this view draws is mixed and tinted: the view's own
     * blend when it draws its own fusion, else its Environment's.
     */
    private pairLook(): PairLook {
        const blend = this.getView().viewFusion?.blend;
        if (!blend) {
            return this.env;
        }
        const mapping = fusionColorMappingById(blend.colorMappingId);
        return {
            viewMode:
                blend.mode === 'checkerboard'
                    ? ImageViewMode.Checkerboard
                    : ImageViewMode.AlphaBlending,
            colorMask: mapping.primaryValues,
            secondaryColorMask: mapping.secondaryValues,
            petFusionColorMapTypeIdx: blend.petColorMapId,
            checkerboardTileSizeMm: blend.checkerboardTileSizeMm,
        };
    }
}
